import clsx from "clsx"
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"

import { faIcon } from "@utils/faIcon"

import Label from "@atoms/Label/Label"
import { Button, IconButton } from "@atoms/index"

import { FormControl, FormField, FormItem, useFormField } from "@molecules/Form/Form"
import InputInternalWrapper from "@molecules/Form/inputs/InputInternalWrapper/InputInternalWrapper"

import styles from "./Input.module.scss"
import { InputName, InputProps } from "./Input.types"

function Input<T extends InputName = InputName>(props: InputProps<T>, ref: React.Ref<HTMLInputElement>) {
    const {
        name,
        id,
        isControlled,
        type = "text",
        value,
        autoFocus = false,

        size,
        className,
        groupPosition = "middle",

        isTabbable = true,

        label = undefined,
        inputHelpText = undefined,
        horizontalHelpText = false,
        indicator,
        prefix = undefined,
        suffix = undefined,

        onClear,

        renderInputComponent = undefined,
        showVisibilityToggle = false,
        button = undefined,

        isDisabled = false,
        isLoading = false,
        isError = false,

        isReadOnly = false,
        isRequired = false,
        isClearable = false,
        isGrouped = false,

        ...rest
    } = props

    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [isValueVisible, setIsValueVisible] = useState(false)
    const [showClearButton, setShowClearButton] = useState<boolean>(false)

    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [])

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus()
        }
    }, [autoFocus])

    const defaultId = useId()

    const labelIsString = typeof label === "string"

    const isInErrorState =
        isError ||
        !!(
            inputHelpText &&
            typeof inputHelpText === "object" &&
            !Array.isArray(inputHelpText) &&
            inputHelpText.status === "error"
        )

    const showValueToggle = type === "password" && showVisibilityToggle

    const handleToggleVisibility = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        setIsValueVisible((isVisible) => !isVisible)
        inputRef.current?.focus()
    }

    const inputType = showValueToggle && isValueVisible ? "text" : type

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        inputRef.current?.focus()

        if (inputRef.current) {
            inputRef.current.value = ""
        }

        onClear?.()

        // Call onChange with empty value for both controlled and uncontrolled inputs
        inputProps.onChange?.({
            target: inputRef.current ?? { value: "" },
        } as React.ChangeEvent<HTMLInputElement>)

        setShowClearButton(false)
    }

    useEffect(() => {
        if (isClearable && isControlled) {
            setShowClearButton(Boolean(value))
        }
    }, [isClearable, isControlled, value])

    const handleCustomInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange?.(event)
    }

    const onInputChangeCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isClearable && !isControlled) {
            setShowClearButton(Boolean(event.target.value))
        }
    }

    const inputProps = {
        ref: inputRef,
        className: clsx(styles.input, className),

        disabled: isDisabled || isLoading,
        readOnly: isReadOnly,
        required: isRequired,

        id: id || defaultId,
        type: type,
        value,
        autoFocus: false,
        tabIndex: isTabbable ? 0 : -1,

        autoComplete: "off",
        spellCheck: false,
        autoCorrect: "off",

        onChangeCapture: onInputChangeCapture,

        ...rest,
        name: name,
    }

    return (
        <InputInternalWrapper
            label={
                label && !isGrouped ? (
                    <Label
                        {...(labelIsString ? { label } : label)}
                        htmlFor={inputProps.id}
                        _isFocused={isFocused}
                        isDisabled={isDisabled}
                        isLoading={isLoading}
                        variant="subtle"
                    />
                ) : undefined
            }
            indicator={indicator}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isError={isInErrorState}
            inputHelpText={isGrouped ? null : inputHelpText}
            horizontalHelpText={horizontalHelpText}
        >
            <div className={styles.inputAndButton}>
                <div
                    ref={containerRef}
                    className={clsx(styles.base, styles[`${size}Size`], {
                        [styles.disabled]: isDisabled || isLoading,
                        [styles.loading]: isLoading,
                        [styles.error]: isInErrorState,
                        [styles.hasSuffix]: !!suffix,
                        [styles.grouped]: isGrouped,
                        [styles.groupedStart]: isGrouped && groupPosition === "start",
                        [styles.groupedMiddle]: isGrouped && groupPosition === "middle",
                        [styles.groupedEnd]: isGrouped && groupPosition === "end",
                    })}
                    onFocusCapture={() => {
                        setIsFocused(true)

                        // Update clear button state for uncontrolled inputs
                        // (handles default values and programmatic changes)
                        if (isClearable && !isControlled && inputRef.current) {
                            setShowClearButton(Boolean(inputRef.current.value))
                        }
                    }}
                    onBlurCapture={(e) => {
                        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
                            setIsFocused(false)
                        }
                    }}
                >
                    {prefix}
                    {renderInputComponent ? (
                        renderInputComponent({
                            ...inputProps,
                            value: inputProps.value as string,
                            onChange: handleCustomInputChange,
                            name: name,
                        })
                    ) : (
                        <input {...inputProps} name={name} type={inputType} />
                    )}
                    {showClearButton && isFocused && (
                        <IconButton
                            size={size === "sm" ? "lg" : "xl"}
                            variant="ghost"
                            icon={faIcon({ name: "circle-xmark", style: "solid" })}
                            aria-label="Clear input"
                            onClick={handleClear}
                            noDebounce={true}
                            isDisabled={isDisabled || isLoading}
                        />
                    )}
                    {showValueToggle && (
                        <IconButton
                            size={size === "sm" ? "lg" : "xl"}
                            variant="ghost"
                            icon={
                                isValueVisible
                                    ? faIcon({ name: "eye-slash", style: "regular" })
                                    : faIcon({ name: "eye", style: "regular" })
                            }
                            aria-label={isValueVisible ? "Hide value" : "Show value"}
                            onClick={handleToggleVisibility}
                            noDebounce={true}
                            isDisabled={isDisabled || isLoading}
                            data-preventInputValidation={true}
                        />
                    )}
                    {suffix}
                </div>
                {button && <Button size={size === "sm" ? "md" : "lg"} {...button} />}
            </div>
        </InputInternalWrapper>
    )
}

const withController = <T extends InputName>(InputComponent: React.FC<InputProps<T>>) => {
    const Controller = forwardRef<HTMLInputElement, InputProps<T>>((props, ref) => {
        const { isControlled = true, ...rest } = props
        const form = useFormContext()

        if (isControlled) {
            return (
                <FormField
                    control={form.control}
                    name={props.name}
                    render={({ field }) => {
                        // In case the value is passed via prop we want this value in sync with react hook form.
                        // A practical example for this is the DateInput component.
                        useEffect(() => {
                            if (props.value !== undefined) {
                                // @ts-expect-error types are tough
                                form.setValue(props.name, props.value)
                            }
                        }, [props.value])

                        const { invalid, error } = useFormField()

                        return (
                            <FormItem>
                                <FormControl>
                                    <InputComponent
                                        {...rest}
                                        {...field}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(event)
                                            rest.onChange?.(event)
                                        }}
                                        isError={invalid || rest.isError}
                                        inputHelpText={
                                            error?.message && !rest.preserveInputHelpText
                                                ? {
                                                      message: String(error.message),
                                                      status: "error",
                                                  }
                                                : rest.inputHelpText
                                        }
                                        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                                            const nextElement = event.relatedTarget as HTMLElement | null
                                            if (nextElement && nextElement.dataset.preventinputvalidation) {
                                                // Skip validation if the clicked element contains preventInputValidation
                                                return
                                            } else {
                                                field.onBlur()
                                                props.onBlur?.(event)
                                            }
                                        }}
                                        isControlled={true}
                                        value={props.value ?? (field.value as string)}
                                        ref={ref}
                                    />
                                </FormControl>
                            </FormItem>
                        )
                    }}
                />
            )
        } else {
            return <InputComponent {...rest} ref={ref} isControlled={false} />
        }
    })

    Controller.displayName = "Input"

    return Controller as typeof Input
}

export default withController(forwardRef(Input))
